<template>
    <Head :title="`PM Plan: ${pmPlan.name}`" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Back Button -->
                <div class="mb-4">
                    <Link
                        :href="route('settings.maintenance.pm-plans.index')"
                        class="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to PM Plans
                    </Link>
                </div>

                <!-- PM Plan Header -->
                <div class="bg-white rounded-lg shadow overflow-hidden mb-6">
                    <div class="p-6">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <div class="flex items-center gap-3">
                                    <h1 class="text-3xl font-bold text-gray-900">{{ pmPlan.name }}</h1>
                                    <InfoButton title="PM Plan Details & Actions" variant="default">
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                            This page displays comprehensive details about a PM Plan template including its schedule
                                            configuration, work order templates, task checklists, and all assets currently using this
                                            plan. PM Plans are reusable templates that automatically generate preventive maintenance
                                            work orders for linked assets at specified intervals.
                                        </p>
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                            • <strong>Quick Stats:</strong> View Total Schedules (assets using this plan), Active
                                              Schedules, Overdue Schedules, and Upcoming Schedules at a glance<br>
                                            • <strong>Status Badges:</strong> Green "Active" means plan generates work orders; gray
                                              "Inactive" means plan is paused<br>
                                            • <strong>Activate/Deactivate:</strong> Toggle plan status without deleting - deactivation
                                              stops future work order generation but preserves history<br>
                                            • <strong>Duplicate:</strong> Create a copy of this plan to use as a starting point for
                                              similar maintenance procedures<br>
                                            • <strong>Edit:</strong> Modify plan details, frequency, tasks, or templates - changes
                                              affect future work orders only<br>
                                            • <strong>Tabs:</strong> Navigate between Details (configuration), Tasks (checklist),
                                              Asset Schedules (linked assets), and History
                                        </p>
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                            • <strong>Total Schedules stat</strong> shows how many assets are using this plan -
                                              useful for measuring plan adoption<br>
                                            • <strong>Overdue Schedules</strong> require immediate attention - click asset name to
                                              view details and create work order<br>
                                            • <strong>Deactivate not delete</strong> - If a plan is no longer needed, deactivate
                                              it to preserve historical data<br>
                                            • <strong>Duplicate for variations</strong> - Instead of creating similar plans from
                                              scratch, duplicate and modify<br>
                                            • <strong>Edit carefully</strong> - Task list changes apply to future work orders but
                                              not existing ones<br>
                                            • <strong>Link to assets</strong> - Navigate to Settings → Maintenance → Assets →
                                              [Asset] → PM Schedule tab to link this plan
                                        </p>
                                    </InfoButton>
                                </div>
                                <p v-if="pmPlan.description" class="mt-2 text-sm text-gray-600">
                                    {{ pmPlan.description }}
                                </p>
                                <div class="mt-3 flex items-center space-x-3">
                                    <span
                                        :class="[
                                            'px-3 py-1 text-sm font-medium rounded-full',
                                            pmPlan.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                        ]"
                                    >
                                        {{ pmPlan.is_active ? 'Active' : 'Inactive' }}
                                    </span>
                                    <span
                                        :class="[
                                            'px-3 py-1 text-sm font-medium rounded-full',
                                            getPriorityClass(pmPlan.priority)
                                        ]"
                                    >
                                        {{ pmPlan.priority_label }}
                                    </span>
                                    <span class="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
                                        {{ pmPlan.frequency_value }} {{ pmPlan.frequency_unit }}
                                    </span>
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div class="flex items-center space-x-2">
                                <button
                                    v-if="$page.props.auth.permissions.includes('maintenance.pm_schedules.manage')"
                                    @click="toggleStatus"
                                    :disabled="toggleForm.processing"
                                    class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <svg v-if="!toggleForm.processing" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                    </svg>
                                    <svg v-else class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {{ pmPlan.is_active ? 'Deactivate' : 'Activate' }}
                                </button>
                                <button
                                    v-if="$page.props.auth.permissions.includes('maintenance.pm_schedules.create')"
                                    @click="duplicatePlan"
                                    :disabled="duplicateForm.processing"
                                    class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <svg v-if="!duplicateForm.processing" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    <svg v-else class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Duplicate
                                </button>
                                <Link
                                    v-if="$page.props.auth.permissions.includes('maintenance.pm_schedules.manage')"
                                    :href="route('settings.maintenance.pm-plans.edit', pmPlan.id)"
                                    class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit
                                </Link>
                            </div>
                        </div>

                        <!-- Quick Stats -->
                        <div class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div class="border-l-4 border-blue-500 pl-4">
                                <p class="text-sm text-gray-600">Total Schedules</p>
                                <p class="text-2xl font-semibold text-gray-900">
                                    {{ statistics.total_schedules }}
                                </p>
                            </div>
                            <div class="border-l-4 border-green-500 pl-4">
                                <p class="text-sm text-gray-600">Active Schedules</p>
                                <p class="text-2xl font-semibold text-gray-900">
                                    {{ statistics.active_schedules }}
                                </p>
                            </div>
                            <div class="border-l-4 border-orange-500 pl-4">
                                <p class="text-sm text-gray-600">Overdue Schedules</p>
                                <p class="text-2xl font-semibold text-gray-900">
                                    {{ statistics.overdue_count }}
                                </p>
                            </div>
                            <div class="border-l-4 border-purple-500 pl-4">
                                <p class="text-sm text-gray-600">Upcoming Schedules</p>
                                <p class="text-2xl font-semibold text-gray-900">
                                    {{ statistics.upcoming_count }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tabs -->
                <div class="bg-white rounded-lg shadow">
                    <div class="border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8 px-6">
                            <button
                                v-for="tab in tabs"
                                :key="tab.id"
                                @click="activeTab = tab.id"
                                :class="[
                                    'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                ]"
                            >
                                {{ tab.label }}
                            </button>
                        </nav>
                    </div>

                    <!-- Tab Content -->
                    <div class="p-6">
                        <!-- Details Tab -->
                        <div v-if="activeTab === 'details'">
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <!-- Schedule Configuration -->
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Schedule Configuration</h3>
                                    <dl class="space-y-3">
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Frequency</dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ pmPlan.frequency_value }} {{ pmPlan.frequency_unit }}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Priority</dt>
                                            <dd class="mt-1">
                                                <span
                                                    :class="[
                                                        'px-2 py-1 text-xs font-medium rounded-full',
                                                        getPriorityClass(pmPlan.priority)
                                                    ]"
                                                >
                                                    {{ pmPlan.priority_label }}
                                                </span>
                                            </dd>
                                        </div>
                                    </dl>
                                </div>

                                <!-- Estimation -->
                                <div>
                                    <h3 class="text-lg font-semibold text-gray-900 mb-4">Estimation</h3>
                                    <dl class="space-y-3">
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Estimated Hours</dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ pmPlan.estimated_hours ? `${pmPlan.estimated_hours} hours` : 'Not specified' }}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt class="text-sm font-medium text-gray-500">Estimated Cost</dt>
                                            <dd class="mt-1 text-sm text-gray-900">
                                                {{ pmPlan.estimated_cost ? formatMoney(pmPlan.estimated_cost) : 'Not specified' }}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>

                            <!-- Work Order Template -->
                            <div class="mt-6">
                                <h3 class="text-lg font-semibold text-gray-900 mb-4">Work Order Template</h3>
                                <div class="bg-gray-50 rounded-lg p-4 space-y-4">
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 mb-2">Title Template</dt>
                                        <dd class="text-sm text-gray-900 font-mono bg-white p-3 rounded border border-gray-200">
                                            {{ pmPlan.work_order_title_template }}
                                        </dd>
                                        <p class="mt-1 text-xs text-gray-500">
                                            Available placeholders: {plan_name}, {asset_name}
                                        </p>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 mb-2">Description Template</dt>
                                        <dd class="text-sm text-gray-900 font-mono bg-white p-3 rounded border border-gray-200 whitespace-pre-wrap">
                                            {{ pmPlan.work_order_description_template }}
                                        </dd>
                                        <p class="mt-1 text-xs text-gray-500">
                                            Available placeholders: {plan_name}, {asset_name}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <!-- Timestamps -->
                            <div class="mt-6">
                                <dl class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500">Created At</dt>
                                        <dd class="mt-1 text-sm text-gray-900">{{ formatDateTime(pmPlan.created_at) }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500">Updated At</dt>
                                        <dd class="mt-1 text-sm text-gray-900">{{ formatDateTime(pmPlan.updated_at) }}</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <!-- Tasks Tab -->
                        <div v-if="activeTab === 'tasks'">
                            <div class="flex items-center justify-between mb-4">
                                <div class="flex items-center gap-3">
                                    <h3 class="text-lg font-semibold text-gray-900">PM Tasks</h3>
                                    <InfoButton title="PM Plan Tasks Checklist" variant="default" size="sm">
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">About this tab</strong><br>
                                            This tab displays the task checklist defined for this PM Plan. These tasks appear on
                                            every work order generated from this plan, ensuring technicians follow consistent
                                            procedures. Tasks are numbered sequentially and should be completed in order.
                                        </p>
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">How tasks work</strong><br>
                                            • <strong>Sequential numbering:</strong> Tasks are numbered 1, 2, 3, etc. in the order
                                              they should be performed<br>
                                            • <strong>Auto-copied to work orders:</strong> When a work order is generated from this
                                              plan, all tasks are copied as a checklist<br>
                                            • <strong>Technician guidance:</strong> Clear task descriptions help technicians perform
                                              maintenance consistently<br>
                                            • <strong>Editing tasks:</strong> Click "Edit" to modify task descriptions or add/remove
                                              tasks - changes apply to future work orders only
                                        </p>
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                            • <strong>Write clear instructions</strong> - Assume the reader may not be familiar with
                                              the equipment<br>
                                            • <strong>One action per task</strong> - Break complex procedures into individual steps<br>
                                            • <strong>Include safety notes</strong> - Add warnings or safety precautions as separate
                                              tasks if needed<br>
                                            • <strong>No tasks = no checklist</strong> - Empty task lists mean work orders will have
                                              no checklist guidance
                                        </p>
                                    </InfoButton>
                                </div>
                                <span class="text-sm text-gray-500">
                                    {{ pmPlan.tasks?.length || 0 }} {{ (pmPlan.tasks?.length || 0) === 1 ? 'task' : 'tasks' }}
                                </span>
                            </div>

                            <div v-if="pmPlan.tasks && pmPlan.tasks.length > 0" class="space-y-3">
                                <div
                                    v-for="task in pmPlan.tasks"
                                    :key="task.id"
                                    class="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg"
                                >
                                    <div class="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-medium text-sm">
                                        {{ task.task_sequence }}
                                    </div>
                                    <div class="flex-1">
                                        <p class="text-sm text-gray-900">{{ task.description }}</p>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-center py-12 bg-gray-50 rounded-lg">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                                <p class="mt-2 text-sm text-gray-500">No tasks defined for this PM plan.</p>
                                <p class="text-xs text-gray-400 mt-1">Edit the plan to add tasks.</p>
                            </div>
                        </div>

                        <!-- Asset Schedules Tab -->
                        <div v-if="activeTab === 'schedules'">
                            <div class="flex items-center justify-between mb-4">
                                <div class="flex items-center gap-3">
                                    <h3 class="text-lg font-semibold text-gray-900">Asset Schedules</h3>
                                    <InfoButton title="Asset Schedules & Linking" variant="default" size="sm">
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">About this tab</strong><br>
                                            This tab shows all assets currently using this PM Plan. Each asset creates a "schedule"
                                            - an instance of this plan with specific dates for when maintenance was last performed
                                            and when it's next due. Schedules are grouped by status: Overdue (past due date) and
                                            Active (upcoming).
                                        </p>
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">How to link this plan to assets</strong><br>
                                            <strong>IMPORTANT:</strong> You cannot link assets to this plan from this page. To add
                                            assets to this PM Plan, follow these steps:<br>
                                            1. <strong>Navigate to Assets:</strong> Go to Settings → Maintenance → Assets<br>
                                            2. <strong>Select an asset:</strong> Click on the asset you want to add PM to<br>
                                            3. <strong>Go to PM Schedule tab:</strong> Switch to the "PM Schedule" tab on the asset
                                               detail page<br>
                                            4. <strong>Add PM Plan:</strong> Click "Add PM Plan" and select this plan from the
                                               dropdown<br>
                                            5. <strong>Automatic scheduling:</strong> The system creates a schedule and calculates
                                               next maintenance date automatically
                                        </p>
                                        <p>
                                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                            • <strong>Overdue schedules</strong> show in red and need immediate attention - create
                                              a work order to address them<br>
                                            • <strong>Click asset name</strong> to jump directly to that asset's detail page<br>
                                            • <strong>Last/Next dates</strong> show when maintenance was last performed and when
                                              it's next due<br>
                                            • <strong>Empty state</strong> means this plan isn't assigned to any assets yet - link
                                              it via the Asset pages<br>
                                            • <strong>Total Schedules count</strong> in Quick Stats shows how widely this plan is
                                              deployed
                                        </p>
                                    </InfoButton>
                                </div>
                                <span class="text-sm text-gray-500">
                                    {{ statistics.total_schedules }} {{ statistics.total_schedules === 1 ? 'schedule' : 'schedules' }}
                                </span>
                            </div>

                            <!-- Overdue Schedules -->
                            <div v-if="overdueSchedules && overdueSchedules.length > 0" class="mb-6">
                                <h4 class="text-sm font-medium text-red-600 mb-3 flex items-center">
                                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Overdue ({{ overdueSchedules.length }})
                                </h4>
                                <div class="space-y-3">
                                    <div
                                        v-for="schedule in overdueSchedules"
                                        :key="schedule.id"
                                        class="flex items-center justify-between p-4 rounded-lg border bg-red-50 border-red-200"
                                    >
                                        <div class="flex-1">
                                            <div class="flex items-center space-x-2">
                                                <Link
                                                    :href="route('settings.maintenance.assets.show', schedule.asset_id)"
                                                    class="font-medium text-blue-600 hover:text-blue-800"
                                                >
                                                    {{ schedule.asset?.name || 'Unknown Asset' }}
                                                </Link>
                                            </div>
                                            <div class="mt-1 flex items-center space-x-4 text-sm text-gray-600">
                                                <span v-if="schedule.last_pm_date">
                                                    Last: {{ formatDate(schedule.last_pm_date) }}
                                                </span>
                                                <span v-if="schedule.next_pm_date" class="text-red-600 font-medium">
                                                    Due: {{ formatDate(schedule.next_pm_date) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Active Schedules -->
                            <div v-if="activeSchedules && activeSchedules.length > 0">
                                <h4 class="text-sm font-medium text-gray-700 mb-3">Active Schedules ({{ activeSchedules.length }})</h4>
                                <div class="space-y-3">
                                    <div
                                        v-for="schedule in activeSchedules"
                                        :key="schedule.id"
                                        class="flex items-center justify-between p-4 rounded-lg border bg-blue-50 border-blue-200"
                                    >
                                        <div class="flex-1">
                                            <div class="flex items-center space-x-2">
                                                <Link
                                                    :href="route('settings.maintenance.assets.show', schedule.asset_id)"
                                                    class="font-medium text-blue-600 hover:text-blue-800"
                                                >
                                                    {{ schedule.asset?.name || 'Unknown Asset' }}
                                                </Link>
                                            </div>
                                            <div class="mt-1 flex items-center space-x-4 text-sm text-gray-600">
                                                <span v-if="schedule.last_pm_date">
                                                    Last: {{ formatDate(schedule.last_pm_date) }}
                                                </span>
                                                <span v-if="schedule.next_pm_date">
                                                    Next: {{ formatDate(schedule.next_pm_date) }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Empty State -->
                            <div v-if="statistics.total_schedules === 0" class="text-center py-12 bg-gray-50 rounded-lg">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                <p class="mt-2 text-sm text-gray-500">This PM plan is not assigned to any assets yet.</p>
                                <p class="text-xs text-gray-400 mt-1">Assign this plan to assets from the Asset management section.</p>
                            </div>
                        </div>

                        <!-- History Tab -->
                        <div v-if="activeTab === 'history'">
                            <div class="text-center py-12">
                                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <h3 class="mt-2 text-sm font-medium text-gray-900">No history available</h3>
                                <p class="mt-1 text-sm text-gray-500">PM plan change history will appear here.</p>
                            </div>
                        </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, useForm, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';
import { useCurrency } from '@/Composables/useCurrency';

const props = defineProps({
    pmPlan: Object,
    creator: Object,
    activeSchedules: Array,
    overdueSchedules: Array,
    upcomingSchedules: Array,
    statistics: Object,
});

const activeTab = ref('details');

const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'schedules', label: 'Asset Schedules' },
    { id: 'history', label: 'History' },
];

const toggleForm = useForm({});
const duplicateForm = useForm({});

// Get currency formatting function
const { formatMoney } = useCurrency();

const getPriorityClass = (priority) => {
    const classes = {
        low: 'bg-blue-100 text-blue-800',
        normal: 'bg-gray-100 text-gray-800',
        high: 'bg-orange-100 text-orange-800',
        urgent: 'bg-red-100 text-red-800',
    };
    return classes[priority] || 'bg-gray-100 text-gray-800';
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const formatDateTime = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const toggleStatus = () => {
    toggleForm.post(route('settings.maintenance.pm-plans.toggle', props.pmPlan.id), {
        preserveScroll: true,
        onSuccess: () => {
            router.reload();
        },
    });
};

const duplicatePlan = () => {
    duplicateForm.post(route('settings.maintenance.pm-plans.duplicate', props.pmPlan.id), {
        onSuccess: (page) => {
            // Redirect to the new duplicated plan
            if (page.props.flash?.pmPlanId) {
                router.visit(route('settings.maintenance.pm-plans.show', page.props.flash.pmPlanId));
            }
        },
    });
};
</script>
